import {
  Column,
  DelayedSpinner,
  UploadFileButton
} from '@cdab/scania/qpr/components/atoms'
import { AttachmentList } from '@cdab/scania/qpr/components/molecules'
import {
  useCssVariableBreakpoint,
  useDeleteDocument,
  useUpdateDocument,
  useUpdateDocumentWithFile
} from '@cdab/scania/qpr/hooks'
import type { AdminReferenceDocument, User } from '@cdab/scania/qpr/schema'
import {
  Roles,
  type DocumentFile,
  type DocumentFileData
} from '@cdab/scania/qpr/schema'
import { freeMemory } from '@cdab/scania/qpr/utils'
import {
  ValidateUrl,
  capitalizeFirstLetter,
  checkStringLength
} from '@cdab/utils'

import {
  TdsButton,
  TdsDropdown,
  TdsDropdownOption,
  TdsMessage,
  TdsRadioButton,
  TdsTextField,
  TdsToast
} from '@scania/tegel-react'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { toast } from 'react-toastify'
import { v4 as uuidv4 } from 'uuid'
import { BeforeDeleteModal } from '../before-delete-modal'
import {
  ButtonsWrapper,
  SpinnerWrapper,
  StyledButtonWrapper,
  StyledColumn,
  StyledFirstButtonWrapper,
  StyledLastButtonWrapper,
  StyledLinkRow,
  StyledMessage,
  StyledRow,
  StyledSectionTitle,
  StyledTextfieldWrapper,
  Title
} from './document-update-form.styles'
import type {
  DocumentUpdateFormProps,
  EditAdminDocumentCreateData
} from './document-update-form.types'

export function DocumentUpdateForm({
  documentData,
  documentFile,
  auditPoints,
  checkPoints,
  allMarkets,
  currentUser
}: DocumentUpdateFormProps) {
  const isLg = useCssVariableBreakpoint('--tds-grid-lg')
  const navigate = useNavigate()
  const { t } = useTranslation(['common', 'documents'])

  const [data, setData] = useState<EditAdminDocumentCreateData>({
    title: documentData.title,
    auditPointsIds: documentData.auditPoints
      .sort((a, b) => a.auditPointNo.localeCompare(b.auditPointNo))
      .map(ap => ap.id),
    checkPointsIds: documentData.checkPoints
      .sort((a, b) => a.checkPointNo.localeCompare(b.checkPointNo))
      .map(cp => cp.id),
    marketId: documentData.market ? documentData.market.id : null,
    url: documentData.url ? documentData.url : ''
  })
  const [stateDocumentName, setStateDocumentName] = useState<boolean>(true)
  const [stateDocumentType, setStateDocumentType] = useState(
    documentFile ? 'File' : 'Link'
  )
  const [stateAuditPoints, setStateAuditPoints] = useState<boolean>(true)
  const [stateCheckPoints, setStateCheckPoints] = useState<boolean>(true)
  const [stateUrl, setStateUrl] = useState<boolean>(true)

  const [stateDocumentFile, setStateDocumentFile] = useState<
    DocumentFileData | undefined
  >(documentFile)

  const [stateFileToUpload, setStateFileToUpload] = useState<
    DocumentFile | undefined
  >(undefined)

  const [
    updateUploadDocumentState,
    handleClickUploadUpdateDocument,
    setUpdateUploadDocumentState
  ] = useUpdateDocumentWithFile(
    {
      ...data,
      id: documentData.id,
      documentVersion: documentData.documentVersion + 1,
      documentFileId: documentData.documentFileId,
      url: stateDocumentType === 'Link' ? data.url : null
    },
    stateFileToUpload
  )

  const [
    updateDocumentState,
    handleClickUpdateDocument,
    setUpdateDocumentState
  ] = useUpdateDocument({
    ...data,
    id: documentData.id,
    documentFileId: documentData.documentFileId,
    documentVersion: documentData.documentVersion + 1
  })

  const [
    deleteDocumentState,
    handleClickDeleteDocument,
    setDeleteDocumentState
  ] = useDeleteDocument(documentData.id)
  const [openDeleteModal, setOpenDeleteModal] = useState(false)

  const isLoading =
    updateUploadDocumentState.status === 'uploading' ||
    updateDocumentState.status === 'loading'

  const isSuccess =
    updateUploadDocumentState.status === 'success' ||
    updateDocumentState.status === 'success' ||
    deleteDocumentState.status === 'success'

  const onDeleteSelectedFile = () => {
    if (stateFileToUpload) {
      freeMemory(stateFileToUpload)

      setStateFileToUpload(undefined)
    }

    if (stateDocumentFile) {
      setStateDocumentFile(undefined)
    }
  }

  const onUploadFileClick = (fileList: FileList) => {
    const files = Array.from(fileList)

    if (files.length === 0) return

    const inputFiles: DocumentFile[] = Array.from(files).map(file => ({
      file,
      id: uuidv4(),
      url: URL.createObjectURL(file)
    }))

    setStateFileToUpload(inputFiles[0])
  }

  function hasAccess(
    currentUser: User,
    documentData: AdminReferenceDocument
  ): boolean {
    return (
      (currentUser.role === Roles.CoOrdinator &&
        documentData.market !== null) ||
      currentUser.role > Roles.CoOrdinator
    )
  }

  function hasFactoryAccess(
    currentUser: User,
    documentData: AdminReferenceDocument
  ): boolean {
    return (
      (currentUser.role >= Roles.CoOrdinator && documentData.market === null) ||
      currentUser.role > Roles.CoOrdinator
    )
  }
  const checkAllData = useCallback(() => {
    return (
      checkStringLength(data.title, 4) &&
      (data.auditPointsIds.length > 0 || data.checkPointsIds.length > 0) &&
      (stateDocumentType === 'File'
        ? stateFileToUpload || stateDocumentFile
        : ValidateUrl(data.url))
    )
  }, [
    data.title,
    data.auditPointsIds.length,
    data.checkPointsIds.length,
    data.url,
    stateDocumentType,
    stateFileToUpload,
    stateDocumentFile
  ])

  const validateAllData = useCallback(() => {
    setStateDocumentName(checkStringLength(data.title, 4))
    setStateAuditPoints(
      data.auditPointsIds.length > 0 || data.checkPointsIds.length > 0
    )
    setStateCheckPoints(
      data.auditPointsIds.length > 0 || data.checkPointsIds.length > 0
    )
    setStateUrl(ValidateUrl(data.url))

    return checkAllData()
  }, [
    data.title,
    data.auditPointsIds.length,
    data.checkPointsIds.length,
    data.url,
    checkAllData
  ])

  const onChangeAuditPoints = useCallback((value: string | null) => {
    if (value === null) return

    // Parse the comma-separated string to get array of selected audit point IDs
    const selectedAuditPointIds = value
      .split(',')
      .filter(id => id.trim() !== '')
      .map(id => parseInt(id.trim()))

    setData(prevData => {
      const hasCheckPoints = prevData.checkPointsIds.length > 0
      setStateAuditPoints(selectedAuditPointIds.length > 0 || hasCheckPoints)
      setStateCheckPoints(selectedAuditPointIds.length > 0 || hasCheckPoints)

      return {
        ...prevData,
        auditPointsIds: selectedAuditPointIds
      }
    })
  }, [])

  const onChangeCheckPoints = useCallback((value: string | null) => {
    // Parse the comma-separated string to get array of selected check point IDs
    if (value === null) return

    const selectedCheckPointIds = value
      .split(',')
      .filter(id => id.trim() !== '')
      .map(id => parseInt(id.trim()))

    setData(prevData => {
      const hasAuditPoints = prevData.auditPointsIds.length > 0
      setStateAuditPoints(selectedCheckPointIds.length > 0 || hasAuditPoints)
      setStateCheckPoints(selectedCheckPointIds.length > 0 || hasAuditPoints)

      return {
        ...prevData,
        checkPointsIds: selectedCheckPointIds
      }
    })
  }, [])

  useEffect(() => {
    return () => {
      freeMemory(stateFileToUpload)
    }
  }, [stateFileToUpload])

  useEffect(() => {
    if (isSuccess) {
      if (updateUploadDocumentState.status === 'success') {
        toast(
          <TdsToast
            variant='success'
            header={t('document-saved', { ns: 'common' })}
            subheader={updateUploadDocumentState.message}
          />
        )
        setUpdateUploadDocumentState({ status: 'none', message: null })
      }

      if (updateDocumentState.status === 'success') {
        toast(
          <TdsToast
            variant='success'
            header={t('document-saved', { ns: 'common' })}
            subheader={updateDocumentState.message}
          />
        )
        setUpdateDocumentState({ status: 'none', message: null })
      }

      if (deleteDocumentState.status === 'success') {
        toast(
          <TdsToast
            variant='success'
            header={t('document-deleted', { ns: 'common' })}
            subheader={deleteDocumentState.message}
          />
        )
        setDeleteDocumentState({ status: 'none', message: null })
      }
      navigate('/admin/documents/all')
    }
  }, [
    deleteDocumentState,
    isSuccess,
    navigate,
    setDeleteDocumentState,
    setUpdateDocumentState,
    setUpdateUploadDocumentState,
    t,
    updateDocumentState,
    updateUploadDocumentState
  ])

  const showUploadButton = () => {
    const shouldShowUploadButton =
      stateDocumentType === 'File' && !stateFileToUpload && !stateDocumentFile

    if (!shouldShowUploadButton) return

    return (
      <UploadFileButton
        multiple={false}
        onUploadFiles={onUploadFileClick}
        className='tds-u-mt3'
        size='sm'
        variant='secondary'
      />
    )
  }

  const showAttachmentList = () => {
    const shouldShowAttachmentsList =
      stateDocumentType === 'File' && stateDocumentFile
    if (!shouldShowAttachmentsList) return

    return (
      <AttachmentList
        modeVariant='secondary'
        fileType='all'
        files={[
          {
            fileName: stateDocumentFile.fileName,
            id: stateDocumentFile.id,
            isImage: false,
            isUploaded: true,
            uploadProgress: 100,
            url: stateDocumentFile.url
          }
        ]}
        hideUploadProgress={true}
        onDeleteAttachment={onDeleteSelectedFile}
        disableDelete={false}
      />
    )
  }

  const showAttachmentListToUpload = () => {
    const shouldShowAttachmentsListToUpload =
      stateDocumentType === 'File' && stateFileToUpload

    if (!shouldShowAttachmentsListToUpload) return

    const currentUploadProgress =
      updateUploadDocumentState.status === 'uploading'
        ? updateUploadDocumentState.progress
        : updateUploadDocumentState.status === 'success' && 100

    return (
      <AttachmentList
        modeVariant='secondary'
        fileType='all'
        files={[
          {
            fileName: stateFileToUpload.file.name,
            id: stateFileToUpload.id,
            isImage: false,
            isUploaded: updateUploadDocumentState.status === 'success',
            uploadProgress: currentUploadProgress || 0,
            url: stateFileToUpload.url
          }
        ]}
        hideUploadProgress={false}
        onDeleteAttachment={onDeleteSelectedFile}
        disableDelete={updateUploadDocumentState.status === 'uploading'}
      />
    )
  }

  const showUrlInput = () => {
    const shouldShowLinkInput = stateDocumentType === 'Link'

    if (!shouldShowLinkInput) return

    return (
      <StyledLinkRow>
        <StyledTextfieldWrapper>
          <TdsTextField
            label='URL'
            modeVariant='secondary'
            labelPosition='outside'
            maxLength={600}
            name='URL'
            placeholder='https//google.com'
            value={data.url ?? ''}
            onTdsInput={e => {
              setData({ ...data, url: e.target.value })
            }}
            onTdsBlur={e => setStateUrl(ValidateUrl(e.target.value))}
            state={!stateUrl ? 'error' : undefined}
            helper={
              !stateUrl ? t('url-notValid', { ns: 'documents' }) : undefined
            }
          />
        </StyledTextfieldWrapper>
        <TdsButton
          size='md'
          variant='secondary'
          text='Open'
          onClick={() => data.url && window.open(data.url, '_blank')}
        />
      </StyledLinkRow>
    )
  }

  return (
    <Column width={12} lg={6} offset={{ lg: 3 }}>
      <StyledRow>
        <Column width={12} lg={6} padding={isLg}>
          <Title>{t('edit-document', { ns: 'documents' })}</Title>
        </Column>
      </StyledRow>
      <StyledRow>
        <StyledColumn width={12} padding={isLg}>
          <TdsTextField
            label={t('name', { ns: 'common' })}
            labelPosition='outside'
            maxLength={200}
            placeholder={t('add-name-for-document', {
              ns: 'documents'
            })}
            modeVariant='secondary'
            value={data.title}
            onTdsChange={e => {
              setData({ ...data, title: e.target.value })
            }}
            onTdsBlur={e =>
              setStateDocumentName(checkStringLength(e.target.value, 4))
            }
            state={!stateDocumentName ? 'error' : undefined}
            helper={
              !stateDocumentName
                ? t('message.validation-error', { chars: '4' })
                : undefined
            }
          />
        </StyledColumn>
      </StyledRow>
      <StyledRow>
        <StyledColumn width={12} lg={6} padding={isLg}>
          <TdsDropdown
            id='auditPointId'
            modeVariant='secondary'
            label={capitalizeFirstLetter(
              t('audit-point', {
                ns: 'common'
              })
            )}
            size='lg'
            labelPosition='outside'
            placeholder={t('select-audit-point', {
              ns: 'documents'
            })}
            defaultValue={data.auditPointsIds.join(',')}
            openDirection={isLg ? 'down' : 'auto'}
            multiselect
            onTdsChange={e => onChangeAuditPoints(e.detail.value)}
            error={!stateAuditPoints}
            helper={
              !stateAuditPoints
                ? t('auditpoint-required', { ns: 'documents' })
                : undefined
            }
          >
            {auditPoints.map(({ id, auditPointNo }) => {
              return (
                <TdsDropdownOption key={id} value={id.toString()}>
                  {auditPointNo}
                </TdsDropdownOption>
              )
            })}
          </TdsDropdown>
        </StyledColumn>
        <StyledColumn width={12} lg={6} padding={isLg}>
          <TdsDropdown
            id='checkPointId'
            label={capitalizeFirstLetter(
              t('checkpoint', {
                ns: 'common'
              })
            )}
            modeVariant='secondary'
            size='lg'
            labelPosition='outside'
            placeholder={t('select-check-point', {
              ns: 'documents'
            })}
            defaultValue={data.checkPointsIds.join(',')}
            openDirection={isLg ? 'down' : 'auto'}
            multiselect
            onTdsChange={e => onChangeCheckPoints(e.detail.value)}
            error={!stateCheckPoints}
            helper={
              !stateCheckPoints
                ? t('checkpoint-required', { ns: 'documents' })
                : undefined
            }
          >
            {checkPoints.map(({ id, checkPointNo }) => {
              return (
                <TdsDropdownOption key={id} value={id.toString()}>
                  {checkPointNo}
                </TdsDropdownOption>
              )
            })}
          </TdsDropdown>
        </StyledColumn>
      </StyledRow>
      <StyledRow>
        <StyledColumn width={12} padding={isLg}>
          <TdsDropdown
            id='marketId'
            label={capitalizeFirstLetter(t('market', { ns: 'common' }))}
            size='lg'
            modeVariant='secondary'
            labelPosition='outside'
            placeholder={t('select-market', { ns: 'documents' })}
            openDirection={isLg ? 'down' : 'auto'}
            defaultValue={
              documentData.market ? documentData.market.id.toString() : 'null'
            }
            disabled={!hasAccess(currentUser, documentData)}
            onTdsChange={e => {
              const value = e.detail.value
              const marketId =
                value === null || value === 'null' ? null : parseInt(value)

              setData({
                ...data,
                marketId: marketId
              })
            }}
          >
            {hasFactoryAccess(currentUser, documentData) && (
              <TdsDropdownOption key='factory' value='null'>
                {'Factory'}
              </TdsDropdownOption>
            )}
            {allMarkets.map(market => (
              <TdsDropdownOption key={market.id} value={market.id.toString()}>
                {market.marketName}
              </TdsDropdownOption>
            ))}
          </TdsDropdown>
        </StyledColumn>
      </StyledRow>
      <StyledRow>
        <StyledColumn width={12} lg={3} padding={isLg}>
          <StyledSectionTitle>
            {t('document-type', { ns: 'documents' })}
          </StyledSectionTitle>
          <TdsRadioButton
            name='document'
            value='File'
            radio-id='document-file'
            required={false}
            checked={stateDocumentType === 'File'}
            onTdsChange={e => setStateDocumentType(e.detail.value)}
          >
            <div slot='label'>{t('file', { ns: 'documents' })}</div>
          </TdsRadioButton>

          <TdsRadioButton
            name='document'
            value='Link'
            radio-id='document-link'
            required={false}
            checked={stateDocumentType === 'Link'}
            onTdsChange={e => setStateDocumentType(e.detail.value)}
          >
            <div slot='label'>{t('link', { ns: 'common' })}</div>
          </TdsRadioButton>
        </StyledColumn>
        <StyledColumn width={12} lg={9} padding={isLg}>
          <>
            {showUploadButton()}
            {showAttachmentList()}
            {showAttachmentListToUpload()}
            {showUrlInput()}
          </>
        </StyledColumn>
      </StyledRow>
      <StyledRow>
        <Column width={12} lg={12} padding={isLg}>
          {updateUploadDocumentState.status === 'error' && (
            <StyledMessage className='tds-u-mt1'>
              <TdsMessage
                header={updateUploadDocumentState.message}
                variant={updateUploadDocumentState.status}
              />
            </StyledMessage>
          )}
          {updateDocumentState.status === 'error' && (
            <StyledMessage className='tds-u-mt1'>
              <TdsMessage
                header={updateDocumentState.message}
                variant={updateDocumentState.status}
              />
            </StyledMessage>
          )}
          {deleteDocumentState.status === 'error' && (
            <StyledMessage className='tds-u-mt1'>
              <TdsMessage
                header={deleteDocumentState.message}
                variant={deleteDocumentState.status}
              />
            </StyledMessage>
          )}
        </Column>
      </StyledRow>
      <StyledRow>
        <Column width={12} lg={12} padding={isLg}>
          <ButtonsWrapper>
            <StyledFirstButtonWrapper>
              <TdsButton
                text={t('cancel', {
                  ns: 'common'
                })}
                variant='secondary'
                onClick={() => navigate('/admin/documents/all')}
              />
            </StyledFirstButtonWrapper>
            {updateUploadDocumentState.status === 'uploading' ? (
              <SpinnerWrapper>
                <DelayedSpinner size='md' />
              </SpinnerWrapper>
            ) : (
              <StyledButtonWrapper>
                <TdsButton
                  disabled={isLoading || isSuccess || !checkAllData()}
                  text={capitalizeFirstLetter(t('save', { ns: 'common' }))}
                  onClick={() => {
                    if (!isLoading && !isSuccess && validateAllData()) {
                      stateDocumentType === 'File'
                        ? handleClickUploadUpdateDocument()
                        : handleClickUpdateDocument()
                    }
                  }}
                />
              </StyledButtonWrapper>
            )}
            <StyledLastButtonWrapper>
              {hasAccess(currentUser, documentData) && (
                <TdsButton
                  text={t('delete', {
                    ns: 'common'
                  })}
                  onClick={() => setOpenDeleteModal(true)}
                  variant='danger'
                />
              )}
            </StyledLastButtonWrapper>
          </ButtonsWrapper>
          <BeforeDeleteModal
            open={openDeleteModal}
            onClose={() => setOpenDeleteModal(false)}
            onSubmit={() => {
              setOpenDeleteModal(false)
              handleClickDeleteDocument()
            }}
          />
        </Column>
      </StyledRow>
    </Column>
  )
}
